/**
 * Filtering and grouping for the Communications view (#623): the log of
 * what was sent to homeowners, newest day first.
 */
import {
  HoaMessageRequest,
  MemberMessageRequest,
  NotificationResponse,
} from '../clients/rust-api-client';
import { ListGroup } from '../shell/GroupedList';

export const EMAIL_OPTIONS = [
  { value: 'sent', label: 'Sent' },
  { value: 'not_sent', label: 'Not sent' },
] as const;

export const READ_OPTIONS = [
  { value: 'read', label: 'Read' },
  { value: 'unread', label: 'Unread' },
] as const;

export interface CommunicationFilters {
  hoas: string[];
  types: string[];
  email: string[];
  read: string[];
  search: string;
}

export const EMPTY_COMMUNICATION_FILTERS: CommunicationFilters = {
  hoas: [],
  types: [],
  email: [],
  read: [],
  search: '',
};

export const applyCommunicationFilters = (
  rows: NotificationResponse[],
  f: CommunicationFilters
): NotificationResponse[] => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((n) => {
    if (f.hoas.length && !f.hoas.includes(n.hoa_id)) return false;
    if (f.types.length && !f.types.includes(n.notification_type)) return false;
    if (f.email.length && !f.email.includes(n.email_sent ? 'sent' : 'not_sent'))
      return false;
    if (f.read.length && !f.read.includes(n.is_read ? 'read' : 'unread'))
      return false;
    if (q) {
      const hay = [
        n.title,
        n.message,
        n.recipient_name,
        n.recipient_email,
        n.hoa_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

const localDayKey = (d: Date): string =>
  [d.getFullYear(), d.getMonth() + 1, d.getDate()]
    .map((x) => String(x).padStart(2, '0'))
    .join('-');

const dayLabel = (d: Date, now: Date): string => {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (localDayKey(d) === localDayKey(now)) return 'Today';
  if (localDayKey(d) === localDayKey(yesterday)) return 'Yesterday';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
};

/** One group per local day the rows were sent, newest first. */
export const groupByDay = (
  rows: NotificationResponse[],
  now: Date = new Date()
): ListGroup<NotificationResponse>[] => {
  const newest = [...rows].sort((a, b) =>
    b.created_at.localeCompare(a.created_at)
  );
  const groups = new Map<string, ListGroup<NotificationResponse>>();
  for (const n of newest) {
    const sent = new Date(n.created_at);
    const key = localDayKey(sent);
    if (!groups.has(key))
      groups.set(key, { key, label: dayLabel(sent, now), items: [] });
    groups.get(key)!.items.push(n);
  }
  return Array.from(groups.values());
};

/**
 * "12:08 PM": the list is already grouped by day, so a row needs only
 * the time. The full timestamp goes in the row's `title`.
 */
export const formatTimeOfDay = (value: string): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

// ---------------------------------------------------------------------------
// Send (#632)
// ---------------------------------------------------------------------------

/** The recipient picker's value for "every member of the association". */
export const ALL_MEMBERS = '__all__';

export const SUBJECT_MAX = 255;

/** What the send modal holds, before it becomes a request. */
export interface MessageDraft {
  hoaId: string | null;
  /** ALL_MEMBERS, or one member's id. */
  recipient: string | null;
  subject: string;
  body: string;
  senderName: string;
  sendEmail: boolean;
}

export type MessageRequest =
  | { kind: 'hoa'; request: HoaMessageRequest }
  | { kind: 'member'; request: MemberMessageRequest };

/** What still stops the draft from being sent, phrased for "Add …". */
export const missingForMessage = (d: MessageDraft): string[] =>
  [
    !d.hoaId && 'an association',
    !d.recipient && 'a recipient',
    !d.subject.trim() && 'a subject',
    !d.body.trim() && 'a message',
  ].filter(Boolean) as string[];

/**
 * The request for a complete draft, or null: to the whole association
 * (`sendHoaMessage`, one row per member) or to one member
 * (`sendMemberMessage`). `send_email` is always explicit — the API
 * defaults it to true, and the modal shows the choice.
 */
export const buildMessageRequest = (d: MessageDraft): MessageRequest | null => {
  if (missingForMessage(d).length || !d.hoaId || !d.recipient) return null;
  const subject = d.subject.trim();
  if (subject.length > SUBJECT_MAX) return null;
  const common = {
    hoa_id: d.hoaId,
    subject,
    message_body: d.body.trim(),
    sender_name: d.senderName,
    send_email: d.sendEmail,
  };
  return d.recipient === ALL_MEMBERS
    ? { kind: 'hoa', request: common }
    : { kind: 'member', request: { ...common, member_id: d.recipient } };
};
